/**
 * Cognito sign-in seam for the AVOWAL portal (client side).
 *
 * Production auth is AWS Cognito user pools. This module wraps
 * `amazon-cognito-identity-js` behind a small surface so the rest of the app
 * never imports the SDK directly and so the whole flow is gated on config:
 *
 *   - `cognitoConfig()` returns the pool config IFF all three env vars are set.
 *     When it returns null, Cognito is "not configured" and the portal falls
 *     back to the DEV-only demo session (see session.ts).
 *   - `cognitoSignIn` performs SRP auth and resolves the id/access tokens.
 *   - `cognitoSignOut` clears the local Cognito session.
 *   - `currentCognitoTokens` reads the cached session (refreshing if needed).
 *
 * Config comes from Vite env (compiled in at build time):
 *   VITE_COGNITO_USER_POOL_ID
 *   VITE_COGNITO_CLIENT_ID
 *   VITE_COGNITO_REGION   (optional; defaults to us-east-1)
 *
 * These are PUBLIC values (pool/client ids are not secrets), but we never
 * hardcode them — absent config simply disables the real sign-in path.
 */
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';

export interface CognitoConfig {
  userPoolId: string;
  clientId: string;
  region: string;
}

export interface CognitoTokens {
  idToken: string;
  accessToken: string;
  /** unix seconds */
  expiresAt: number;
  email: string;
}

let _config: CognitoConfig | null | undefined;

/**
 * Returns the Cognito config when fully configured, else null.
 * Result is memoized for the lifetime of the page.
 */
export function cognitoConfig(): CognitoConfig | null {
  if (_config !== undefined) return _config;
  const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
  const region = import.meta.env.VITE_COGNITO_REGION ?? 'us-east-1';
  if (userPoolId && clientId) {
    _config = { userPoolId, clientId, region };
  } else {
    _config = null;
  }
  return _config;
}

/** True when a real Cognito pool is configured for this build. */
export function cognitoEnabled(): boolean {
  return cognitoConfig() !== null;
}

let _pool: CognitoUserPool | null = null;
function pool(cfg: CognitoConfig): CognitoUserPool {
  if (!_pool) {
    _pool = new CognitoUserPool({ UserPoolId: cfg.userPoolId, ClientId: cfg.clientId });
  }
  return _pool;
}

function sessionToTokens(session: CognitoUserSession, fallbackEmail: string): CognitoTokens {
  const idToken = session.getIdToken();
  const payload = idToken.decodePayload() as Record<string, unknown>;
  const email = typeof payload['email'] === 'string' ? payload['email'] : fallbackEmail;
  return {
    idToken: idToken.getJwtToken(),
    accessToken: session.getAccessToken().getJwtToken(),
    expiresAt: idToken.getExpiration(),
    email,
  };
}

/**
 * Sign in with email + password (USER_SRP_AUTH). Resolves the tokens or rejects
 * with an Error carrying a human-readable message. Throws if Cognito is not
 * configured — callers should guard on `cognitoEnabled()`.
 */
export function cognitoSignIn(email: string, password: string): Promise<CognitoTokens> {
  const cfg = cognitoConfig();
  if (!cfg) return Promise.reject(new Error('Cognito is not configured'));
  const user = new CognitoUser({ Username: email, Pool: pool(cfg) });
  const details = new AuthenticationDetails({ Username: email, Password: password });
  return new Promise<CognitoTokens>((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: (session) => resolve(sessionToTokens(session, email)),
      onFailure: (err: unknown) => reject(toError(err)),
      newPasswordRequired: () =>
        reject(new Error('A new password is required. Complete setup, then sign in again.')),
    });
  });
}

/** Clears the locally cached Cognito session for the current user. */
export function cognitoSignOut(): void {
  const cfg = cognitoConfig();
  if (!cfg) return;
  const user = pool(cfg).getCurrentUser();
  if (user) user.signOut();
}

/**
 * Outcome of reading/refreshing the cached Cognito session.
 *   - 'tokens'   — a live (possibly refreshed) session; carries the tokens.
 *   - 'none'     — the SDK reports no signed-in user (genuine signed-out state).
 *   - 'error'    — a transient failure (e.g. refresh network error); the caller
 *                  should NOT treat this as a sign-out and should keep any live
 *                  token it already has.
 */
export type CurrentTokens =
  | { status: 'tokens'; tokens: CognitoTokens }
  | { status: 'none' }
  | { status: 'error' };

/**
 * Reads the current Cognito session (refreshing via the cached refresh token if
 * the access/id token has expired). Distinguishes "no signed-in user" from a
 * transient refresh error so rehydration on reload doesn't spuriously sign a
 * user out when the network blips.
 */
export function currentCognitoTokens(): Promise<CurrentTokens> {
  const cfg = cognitoConfig();
  if (!cfg) return Promise.resolve({ status: 'none' });
  const user = pool(cfg).getCurrentUser();
  if (!user) return Promise.resolve({ status: 'none' });
  return new Promise<CurrentTokens>((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err) {
        // Refresh failed (network, refresh-token revoked is surfaced as an error
        // too, but we err toward keeping the live mirror rather than bouncing
        // the user mid-session). Treat as transient.
        resolve({ status: 'error' });
        return;
      }
      if (!session || !session.isValid()) {
        resolve({ status: 'none' });
        return;
      }
      resolve({ status: 'tokens', tokens: sessionToTokens(session, '') });
    });
  });
}

function toError(err: unknown): Error {
  if (err instanceof Error) return err;
  if (err && typeof err === 'object' && 'message' in err) {
    const m = (err as { message?: unknown }).message;
    if (typeof m === 'string') return new Error(m);
  }
  return new Error('Sign-in failed');
}
